/**
 * liveEvents.js - this tab's one connection to GET /api/events, the server's
 * stream of "something changed" notices (server/lib/liveEvents.js). It is what
 * lets a new opportunity, an edit or a calendar change show up on everyone's
 * screen without a refresh.
 *
 * fetch() and a stream reader, not EventSource: EventSource cannot send the
 * Authorization header and every /api route needs one. Going through fetch
 * means the wrapper in src/index.js signs it like any other call.
 *
 * Wire format (server-sent events):
 *   event: hello   data: {"seq":N}       once per connection
 *   event: change  data: {seq, kind, id, sub, method, actor, actorEmail, clientId, fields, at}
 *   : ping                               every 25 s, keeps proxies from closing it
 *
 * Screens never talk to this directly: they use useLive.js. What it promises:
 *   - one connection per tab, opened by startLive() (calling it again is a no-op)
 *   - it reconnects after a drop, waiting 1 s, then 2, 4 ... up to 30 s; a hello
 *     resets the wait
 *   - when a connection comes back after a drop, subscribers get
 *     { kind: '*', resync: true }: whatever changed while it was down was never
 *     sent, so the screens re-read
 *   - this tab's own changes are not delivered (clientId === CLIENT_ID): the
 *     screen that saved already shows them
 *   - waking up (the tab shown again, or the network back) redials at once
 *     instead of waiting out the backoff, and after WAKE_RESYNC_MS or more
 *     away it resyncs too: a background tab the browser froze or slept can
 *     miss changes without its stream ever looking dropped (10/7/26 - a Won
 *     opportunity did not show for Mohammed until he refreshed)
 */
import { CLIENT_ID } from './clientId';

const EVENTS_URL = '/api/events';
const RETRY_FIRST_MS = 1000;
const RETRY_MAX_MS = 30000;
// The server pings every 25 s. Nothing at all for this long means the
// connection died without closing (a laptop that slept, a dropped proxy), and
// a read would wait forever - so give up on it and dial again.
const SILENCE_MS = 65000;
// away this long (tab hidden) and waking re-reads everything
const WAKE_RESYNC_MS = 60000;

const subscribers = new Set();
let conn = null; // the running connection's state, null when stopped

/**
 * Split buffered stream text into complete frames. Pure; exported for tests.
 * Returns { frames: [{ event, data, id }], rest } - `rest` is an unfinished
 * frame to keep until more text arrives. Comment lines (": ping") and frames
 * without data (retry:) carry nothing and are dropped.
 */
export function parseFrames(buffer) {
  const frames = [];
  let text = String(buffer || '');
  let end = text.indexOf('\n\n');
  while (end >= 0) {
    const block = text.slice(0, end);
    text = text.slice(end + 2);
    const frame = { event: 'message', data: '', id: null };
    const data = [];
    for (const raw of block.split('\n')) {
      const line = raw.replace(/\r$/, '');
      if (!line || line.startsWith(':')) continue;
      const colon = line.indexOf(':');
      const field = colon < 0 ? line : line.slice(0, colon);
      let value = colon < 0 ? '' : line.slice(colon + 1);
      if (value.startsWith(' ')) value = value.slice(1);
      if (field === 'event') frame.event = value;
      else if (field === 'data') data.push(value);
      else if (field === 'id') frame.id = value;
    }
    if (data.length) {
      frame.data = data.join('\n');
      frames.push(frame);
    }
    end = text.indexOf('\n\n');
  }
  return { frames, rest: text };
}

function deliver(evt) {
  for (const fn of [...subscribers]) {
    try {
      fn(evt);
    } catch (e) {
      console.warn('live event handler failed:', e && e.message);
    }
  }
}

function handleFrame(c, frame) {
  if (frame.event === 'hello') {
    c.retryMs = RETRY_FIRST_MS;
    if (c.dropped) {
      c.dropped = false;
      deliver({ kind: '*', resync: true });
    }
    return;
  }
  if (frame.event !== 'change') return;
  let evt;
  try {
    evt = JSON.parse(frame.data);
  } catch {
    return;
  }
  if (!evt || typeof evt !== 'object') return;
  if (evt.clientId && evt.clientId === CLIENT_ID) return;
  deliver(evt);
}

async function connect(c) {
  c.timer = null;
  const controller = new AbortController();
  c.controller = controller;
  const quiet = () => {
    clearTimeout(c.silence);
    c.silence = setTimeout(() => controller.abort(), SILENCE_MS);
  };
  try {
    quiet();
    const res = await fetch(EVENTS_URL, {
      headers: { Accept: 'text/event-stream' },
      cache: 'no-store',
      signal: controller.signal,
    });
    if (!res.ok || !res.body || typeof res.body.getReader !== 'function') throw new Error(`HTTP ${res.status}`);
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done || c.stopped) break;
      quiet();
      buffer += decoder.decode(value, { stream: true });
      const out = parseFrames(buffer);
      buffer = out.rest;
      for (const frame of out.frames) handleFrame(c, frame);
    }
  } catch {
    /* refused, dropped or given up on: all the same - dial again */
  }
  clearTimeout(c.silence);
  if (c.stopped || conn !== c) return;
  c.dropped = true;
  c.timer = setTimeout(() => connect(c), c.retryMs);
  c.retryMs = Math.min(c.retryMs * 2, RETRY_MAX_MS);
}

/** Redial now if a reconnect is waiting; `resync` also re-reads everything. */
function wake(resync) {
  const c = conn;
  if (!c || c.stopped) return;
  if (c.timer) {
    clearTimeout(c.timer);
    c.retryMs = RETRY_FIRST_MS;
    connect(c); // its hello resyncs: the connection had dropped
  } else if (resync) {
    deliver({ kind: '*', resync: true });
  }
}
let hiddenAt = null;
function onVisibility() {
  if (document.visibilityState === 'hidden') {
    hiddenAt = Date.now();
    return;
  }
  const away = hiddenAt == null ? 0 : Date.now() - hiddenAt;
  hiddenAt = null;
  wake(away >= WAKE_RESYNC_MS);
}
const onOnline = () => wake(true);
function listen(on) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  const fn = on ? 'addEventListener' : 'removeEventListener';
  document[fn]('visibilitychange', onVisibility);
  window[fn]('online', onOnline);
}

/** Open this tab's connection. Idempotent: a second call while it runs does nothing. */
export function startLive() {
  if (conn) return;
  // Every browser GlobalOps supports has these; a test environment may not.
  if (typeof fetch !== 'function' || typeof TextDecoder !== 'function' || typeof AbortController !== 'function') return;
  conn = { stopped: false, dropped: false, retryMs: RETRY_FIRST_MS, timer: null, silence: null, controller: null };
  hiddenAt = null;
  listen(true);
  connect(conn);
}

/** Close the connection and cancel any pending reconnect (sign-out, tests). */
export function stopLive() {
  const c = conn;
  if (!c) return;
  conn = null;
  c.stopped = true;
  listen(false);
  clearTimeout(c.timer);
  clearTimeout(c.silence);
  try {
    if (c.controller) c.controller.abort();
  } catch {
    /* already closed */
  }
}

/** Get every live event (and resyncs) this tab receives. Returns unsubscribe. */
export function subscribe(fn) {
  subscribers.add(fn);
  return () => {
    subscribers.delete(fn);
  };
}
